import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTemuPageShopScope, stackShopDimensionTabs } from '@/temu-platform/hooks/useTemuPageShopScope';
import TemuOpsListToolbar, { downloadCsv } from '@/temu-platform/components/TemuOpsListToolbar';
import TemuSyncProgressSlot from '@/temu-platform/components/TemuSyncProgressSlot';
import TemuGatewayRegionTabs, { TEMU_GATEWAY_REGION_ALL } from '@/temu-platform/components/TemuGatewayRegionTabs';
import { ProductFilterCard, ProductListCard } from '@/temu-platform/modules/products/components/ProductPageCards';
import { useGuardedRefresh } from '@/temu-platform/hooks/useGuardedRefresh';
import { useTemuSyncJobs } from '@/temu-platform/context/TemuSyncJobContext';
import { temuPlatformAPI } from '@/temu-platform/api/temuPlatformApi';
import { formatSiteListSyncedAt } from '@/temu-platform/utils/temuSiteList';
import { fetchAllPages } from '@/temu-platform/utils/temuExportHelpers';
import { buildOpsExportMenu, opsExportLabel } from '@/temu-platform/utils/buildOpsExportMenu';
import TemuStockMutationModal from '@/temu-platform/modules/ops/TemuStockMutationModal';
import { showToast } from '@/utils/toast';
import '@/styles/temu-platform/temu-product-ops.css';

const STOCK_SYNC_KINDS = ['stock'];

function extractListPayload(res) {
  if (!res || typeof res !== 'object') {
    return { items: [], total: 0, lastSyncedAt: null, neverSynced: true };
  }
  const items = Array.isArray(res.items)
    ? res.items
    : (Array.isArray(res.content) ? res.content : (Array.isArray(res.data) ? res.data : []));
  const total = Number.isFinite(Number(res.totalElements))
    ? Number(res.totalElements)
    : (Number.isFinite(Number(res.total)) ? Number(res.total) : items.length);
  const lastSyncedAt = res.lastSyncedAt || res.syncedAt || null;
  const neverSynced = Boolean(res.neverSynced) || (!lastSyncedAt && items.length === 0);
  return { items, total, lastSyncedAt, neverSynced };
}

/**
 * @param {{ track: 'full'|'semi'|'local', title?: string }} props
 */
export default function TemuStockListPage({
  track = 'full',
  title,
}) {
  const { t } = useTranslation('workbench');
  const isSemi = track === 'semi' || track === 'local';
  const pageTitle = title || (isSemi ? t('platformNav.stock') : t('stock.jitTitle'));
  const keywordPlaceholder = isSemi ? t('stock.kwSemi') : t('stock.kwJitTemu');

  const { shopDimensionTabs, concreteScopeId } = useTemuPageShopScope();
  const shopId = concreteScopeId || '';
  const { runBlocking, isBusy } = useTemuSyncJobs();
  const [gatewayRegion, setGatewayRegion] = useState(TEMU_GATEWAY_REGION_ALL);
  const [filterDraft, setFilterDraft] = useState({ keyword: '' });
  const [appliedKeyword, setAppliedKeyword] = useState('');
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [lastSyncedAt, setLastSyncedAt] = useState(null);
  const [neverSynced, setNeverSynced] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(() => new Set());
  const [mutationOpen, setMutationOpen] = useState(false);
  const [mutationMode, setMutationMode] = useState('edit');
  const [mutationRow, setMutationRow] = useState(null);
  const [mutationBusy, setMutationBusy] = useState(false);

  const listGatewayRegion = isSemi && gatewayRegion && gatewayRegion !== TEMU_GATEWAY_REGION_ALL
    ? gatewayRegion
    : undefined;

  const lastSyncedLabel = useMemo(() => {
    if (!lastSyncedAt) return t('tiktokCatalog.notSynced');
    try {
      return t('marketing.syncedAt', { time: formatSiteListSyncedAt(lastSyncedAt) });
    } catch {
      return t('marketing.syncedAt', { time: String(lastSyncedAt) });
    }
  }, [lastSyncedAt, t]);

  const loadList = useCallback(async (sid, kw) => {
    if (!sid) {
      setRows([]);
      setTotal(0);
      setNeverSynced(true);
      setLastSyncedAt(null);
      return;
    }
    if (typeof temuPlatformAPI.listStock !== 'function') {
      setRows([]);
      setTotal(0);
      setNeverSynced(true);
      setLastSyncedAt(null);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await temuPlatformAPI.listStock(sid, {
        page: 0,
        size: 50,
        keyword: kw || undefined,
        track,
        gatewayRegion: isSemi && gatewayRegion !== TEMU_GATEWAY_REGION_ALL ? gatewayRegion : undefined,
      });
      const payload = extractListPayload(res);
      setRows(payload.items);
      setTotal(payload.total);
      setLastSyncedAt(payload.lastSyncedAt);
      setNeverSynced(payload.neverSynced);
      setSelected(new Set());
    } catch (e) {
      setRows([]);
      setTotal(0);
      setSelected(new Set());
      setError(e?.message || t('stock.loadFail'));
      showToast(e?.message || t('stock.loadFail'), 'error');
    } finally {
      setLoading(false);
    }
  }, [track, isSemi, gatewayRegion, t]);

  useEffect(() => {
    loadList(shopId, appliedKeyword);
  }, [shopId, appliedKeyword, loadList]);

  useEffect(() => {
    setGatewayRegion(TEMU_GATEWAY_REGION_ALL);
  }, [shopId]);

  const onQuery = () => {
    setAppliedKeyword(String(filterDraft.keyword || '').trim());
  };

  const onReset = () => {
    setFilterDraft({ keyword: '' });
    setAppliedKeyword('');
  };

  const handleSync = useCallback(async () => {
    if (!shopId) {
      showToast(t('marketing.pickTemu'), 'warning');
      return;
    }
    if (typeof temuPlatformAPI.syncStock !== 'function') {
      showToast(t('stock.syncApiNotReady'), 'info');
      return;
    }
    try {
      await runBlocking({
        kind: 'stock',
        shopId,
        label: t('publishRecord.syncUpdate'),
        syncFn: () => temuPlatformAPI.syncStock(shopId, {
          track,
          gatewayRegion: listGatewayRegion,
        }),
        buildSuccess: (res) => ({
          imported: Number(res?.imported ?? res?.total ?? 0) || 0,
          message: t('stock.syncDone', { title: pageTitle }),
        }),
      });
      await loadList(shopId, appliedKeyword);
    } catch (e) {
      if (e?.code === 'SYNC_IN_PROGRESS' || e?.code === 'ABORT') return;
      // runBlocking 已提示
    }
  }, [shopId, track, pageTitle, appliedKeyword, loadList, runBlocking, listGatewayRegion, t]);

  const {
    refresh: onSync,
    refreshing: syncingLocal,
    cooldownSeconds: syncCooldown,
  } = useGuardedRefresh(handleSync, { minIntervalMs: 5000 });

  const syncing = syncingLocal || isBusy('stock', shopId);

  const emptyTitle = useMemo(() => {
    if (!shopId) return t('returnList.pickShop');
    if (loading) return t('returnList.loading');
    if (neverSynced && rows.length === 0) return t('stock.neverStock');
    if (rows.length === 0) return t('stock.noMatch');
    return t('freightLib.noData');
  }, [shopId, loading, neverSynced, rows.length, t]);

  const emptyHint = useMemo(() => {
    if (!shopId) return '';
    if (neverSynced && rows.length === 0 && !loading) {
      return t('stock.pullHintTemu');
    }
    if (rows.length === 0 && !loading && appliedKeyword) {
      return t('marketing.adjustFilter');
    }
    return '';
  }, [shopId, neverSynced, rows.length, loading, appliedKeyword, t]);

  const colSpan = (isSemi ? 6 : 3) + 2;

  const rowKey = (row) => String(row.id || `${row.productSkuId}-${row.warehouseId || row.productSkcId}`);

  const toggleRow = (key) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === rows.length) {
      setSelected(new Set());
      return;
    }
    setSelected(new Set(rows.map((r) => rowKey(r))));
  };

  const resolveSelectedRows = () => rows.filter((row) => selected.has(rowKey(row)));

  const clearSelection = () => setSelected(new Set());

  const stockColumns = isSemi
    ? [
      { key: 'sku', label: 'SKU', pick: (r) => r.productSkuId ?? '—' },
      { key: 'warehouseId', label: t('stock.colWarehouse'), pick: (r) => r.warehouseId || '—' },
      { key: 'warehouseName', label: t('stock.colWhName'), pick: (r) => r.warehouseName || '—' },
      { key: 'qty', label: t('productTable.stock'), pick: (r) => r.skuStockQuantity ?? '—' },
      { key: 'preSale', label: t('stock.colPre'), pick: (r) => r.preSaleStockQuantity ?? '—' },
      { key: 'enablePreSale', label: t('stock.colPreFlag'), pick: (r) => (r.enablePreSale == null ? '—' : (r.enablePreSale ? t('productTemplate.on') : t('productTemplate.off'))) },
    ]
    : [
      { key: 'skc', label: 'SKC', pick: (r) => r.productSkcId ?? '—' },
      { key: 'sku', label: 'SKU', pick: (r) => r.productSkuId ?? '—' },
      { key: 'qty', label: t('productTable.stock'), pick: (r) => r.skuStockQuantity ?? '—' },
    ];

  const exportRows = (source) => {
    const list = Array.isArray(source) ? source : [];
    if (!list.length) {
      showToast(t('rrpOps.noneExport'), 'warning');
      return;
    }
    const headers = stockColumns.map((c) => c.label);
    const body = list.map((row) => {
      const o = {};
      stockColumns.forEach((col) => {
        o[col.label] = col.pick(row);
      });
      return o;
    });
    downloadCsv(`temu-stock-${track}-${Date.now()}.csv`, headers, body);
    showToast(t('stock.exported', { count: list.length }), 'success');
  };

  const exportFiltered = async () => {
    if (!shopId) {
      showToast(t('marketing.pickTemu'), 'warning');
      return;
    }
    if (typeof temuPlatformAPI.listStock !== 'function') {
      showToast(t('stock.listApiNotReady'), 'info');
      return;
    }
    if (exporting) return;
    setExporting(true);
    try {
      const kw = appliedKeyword || undefined;
      const all = await fetchAllPages(async (pageNo, size) => {
        const res = await temuPlatformAPI.listStock(shopId, {
          page: Math.max(0, pageNo - 1),
          size,
          keyword: kw,
          track,
          gatewayRegion: listGatewayRegion,
        });
        const payload = extractListPayload(res);
        return { items: payload.items, total: payload.total };
      });
      exportRows(all);
    } catch (e) {
      showToast(e?.message || t('tiktokCatalog.exportFilterFailed'), 'error');
    } finally {
      setExporting(false);
    }
  };

  const copySelectedIds = async () => {
    const list = resolveSelectedRows();
    if (!list.length) {
      showToast(t('stock.pickRows'), 'warning');
      return;
    }
    const ids = list.map((row) => {
      if (isSemi) return String(row.productSkuId || rowKey(row));
      return String(row.productSkcId || row.productSkuId || rowKey(row));
    }).filter(Boolean);
    const unique = [...new Set(ids)];
    const text = unique.join('\n');
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement('textarea');
        ta.value = text;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      showToast(t('stock.copiedIds', { count: unique.length }), 'success');
    } catch {
      showToast(t('freightLib.copyFailed'), 'error');
    }
  };

  const openEdit = (row) => {
    setMutationMode('edit');
    setMutationRow(row);
    setMutationOpen(true);
  };

  const openAdd = () => {
    setMutationMode('add');
    setMutationRow(null);
    setMutationOpen(true);
  };

  const onMutationConfirm = async (result) => {
    if (result?.error) {
      showToast(result.error, 'warning');
      return;
    }
    if (!shopId || !result?.payload) return;
    setMutationBusy(true);
    try {
      if (mutationMode === 'add') {
        if (typeof temuPlatformAPI.addSemiRouteStock !== 'function') {
          showToast(t('stock.addApiNotReady'), 'info');
          return;
        }
        await temuPlatformAPI.addSemiRouteStock(shopId, result.payload);
        showToast(t('stock.added'), 'success');
      } else if (isSemi) {
        if (typeof temuPlatformAPI.updateSemiStock !== 'function') {
          showToast(t('stock.semiApiNotReady'), 'info');
          return;
        }
        await temuPlatformAPI.updateSemiStock(shopId, result.payload);
        showToast(t('stock.updated'), 'success');
      } else {
        if (typeof temuPlatformAPI.editJitStock !== 'function') {
          showToast(t('stock.jitApiNotReady'), 'info');
          return;
        }
        await temuPlatformAPI.editJitStock(shopId, result.payload);
        showToast(t('stock.jitUpdated'), 'success');
      }
      setMutationOpen(false);
      await loadList(shopId, appliedKeyword);
    } catch (e) {
      showToast(e?.message || t('stock.opFail'), 'error');
    } finally {
      setMutationBusy(false);
    }
  };

  const batchOpsMenu = useMemo(() => ([
    ...(isSemi ? [{
      key: 'add-route-stock',
      label: t('stock.addRoute'),
      disabled: !shopId,
      onClick: openAdd,
    }] : []),
    {
      key: 'export-selected',
      label: t('marketing.exportSelected'),
      disabled: selected.size === 0,
      onClick: () => exportRows(resolveSelectedRows()),
    },
    {
      key: 'copy-ids',
      label: isSemi ? t('stock.copySku') : t('stock.copySkcSku'),
      disabled: selected.size === 0,
      onClick: copySelectedIds,
    },
    {
      key: 'clear-selection',
      label: t('marketing.clearSel'),
      disabled: selected.size === 0,
      onClick: clearSelection,
    },
  ]), [selected.size, isSemi, rows, track, shopId, t]);

  return (
    <>
      <TemuSyncProgressSlot kinds={STOCK_SYNC_KINDS} />
      <ProductFilterCard
        toolbar={(
          <TemuOpsListToolbar
            selectedCount={selected.size}
            busy={loading || syncing || exporting}
            syncKinds={STOCK_SYNC_KINDS}
            quotaKind="stock"
            onSync={onSync}
            syncing={syncing}
            syncCooldownSeconds={syncCooldown}
            syncDisabled={!shopId || syncing}
            lastSyncedLabel={lastSyncedLabel}
            syncHint={t('stock.syncHintTemu', { title: pageTitle })}
            exportLabel={opsExportLabel(exporting)}
            exportDisabled={!shopId || exporting}
            exportMenu={buildOpsExportMenu({
              exporting,
              filteredCount: total,
              pageCount: rows.length,
              selectedCount: selected.size,
              onExportFiltered: exportFiltered,
              onExportPage: () => exportRows(rows),
              onExportSelected: () => exportRows(resolveSelectedRows()),
            })}
            batchOpsMenu={batchOpsMenu}
          />
        )}
      >
        <div className="tpo-filter-bar">
          <div className="tpo-filter-bar__fields">
            <div className="tpo-field tpo-field--inline tpo-field--w300">
              <span className="tpo-field__label">{t('productTemplate.keyword')}</span>
              <input
                className="tpo-input"
                type="search"
                value={filterDraft.keyword}
                disabled={!shopId}
                placeholder={keywordPlaceholder}
                onChange={(e) => setFilterDraft({ keyword: e.target.value })}
                onKeyDown={(e) => e.key === 'Enter' && onQuery()}
              />
            </div>
          </div>
          <div className="tpo-filter-bar__actions">
            <button
              type="button"
              className="tpo-btn tpo-btn--secondary tpo-btn--sm"
              disabled={!shopId}
              onClick={onReset}
            >
              {t('shop.reset')}
            </button>
            <button
              type="button"
              className="tpo-btn tpo-btn--primary tpo-btn--sm"
              disabled={!shopId || loading}
              onClick={onQuery}
            >
              {t('shop.query')}
            </button>
          </div>
        </div>
        {error ? <div className="tpo-ops-error" role="alert">{error}</div> : null}
      </ProductFilterCard>

      <ProductListCard
        tabs={isSemi
          ? stackShopDimensionTabs(shopDimensionTabs, (
            <TemuGatewayRegionTabs
              shopId={shopId}
              value={gatewayRegion}
              onChange={(next) => setGatewayRegion(next)}
            />
          ))
          : shopDimensionTabs}
        loading={Boolean(shopId) && loading}
        loadingLabel={t('stock.querying')}
      >
        <div className="tpo-drafts__table-wrap tpo-ops-table-wrap">
          <table className="tpo-publish__table tpo-drafts__table tpo-ops-table">
            <thead>
              <tr>
                <th scope="col" className="tpo-ops-th tpo-ops-th--check temu-product-cell--spu-check">
                  <input
                    type="checkbox"
                    checked={rows.length > 0 && selected.size === rows.length}
                    onChange={toggleAll}
                    aria-label={t('productTemplate.selectAll')}
                    disabled={!rows.length}
                  />
                </th>
                {isSemi ? (
                  <>
                    <th scope="col">SKU</th>
                    <th scope="col">{t('stock.colWarehouse')}</th>
                    <th scope="col">{t('stock.colWhName')}</th>
                    <th scope="col">{t('productTable.stock')}</th>
                    <th scope="col">{t('stock.colPre')}</th>
                    <th scope="col">{t('stock.colPreFlag')}</th>
                    <th scope="col">{t('productTable.actions')}</th>
                  </>
                ) : (
                  <>
                    <th scope="col">SKC</th>
                    <th scope="col">SKU</th>
                    <th scope="col">{t('productTable.stock')}</th>
                    <th scope="col">{t('productTable.actions')}</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {!shopId || loading || rows.length === 0 ? (
                <tr>
                  <td colSpan={colSpan} className="tpo-certs-empty-cell">
                    <div className="tpo-certs-empty">
                      <p className="tpo-certs-empty__title">{emptyTitle}</p>
                      {emptyHint ? <p className="tpo-certs-empty__hint">{emptyHint}</p> : null}
                    </div>
                  </td>
                </tr>
              ) : (
                rows.map((row) => {
                  const key = rowKey(row);
                  return (
                  <tr key={key}>
                    <td className="tpo-ops-td tpo-ops-td--check temu-product-cell--spu-check">
                      <input
                        type="checkbox"
                        checked={selected.has(key)}
                        onChange={() => toggleRow(key)}
                        aria-label={t('orderList.selectRow', { title: key })}
                      />
                    </td>
                    {isSemi ? (
                      <>
                        <td>{row.productSkuId ?? '—'}</td>
                        <td>{row.warehouseId || '—'}</td>
                        <td>{row.warehouseName || '—'}</td>
                        <td>{row.skuStockQuantity ?? '—'}</td>
                        <td>{row.preSaleStockQuantity ?? '—'}</td>
                        <td>{row.enablePreSale == null ? '—' : (row.enablePreSale ? t('productTemplate.on') : t('productTemplate.off'))}</td>
                        <td>
                          <button
                            type="button"
                            className="tpo-btn tpo-btn--ghost tpo-btn--sm"
                            disabled={!shopId || mutationBusy || !row.productSkuId || !row.warehouseId}
                            onClick={() => openEdit(row)}
                          >
                            {t('productTable.edit')}
                          </button>
                        </td>
                      </>
                    ) : (
                      <>
                        <td>{row.productSkcId ?? '—'}</td>
                        <td>{row.productSkuId ?? '—'}</td>
                        <td>{row.skuStockQuantity ?? '—'}</td>
                        <td>
                          <button
                            type="button"
                            className="tpo-btn tpo-btn--ghost tpo-btn--sm"
                            disabled={!shopId || mutationBusy || !row.productSkcId || !row.productSkuId}
                            onClick={() => openEdit(row)}
                          >
                            {t('productTable.edit')}
                          </button>
                        </td>
                      </>
                    )}
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </ProductListCard>

      <TemuStockMutationModal
        open={mutationOpen}
        mode={mutationMode}
        track={track}
        row={mutationRow}
        confirming={mutationBusy}
        onConfirm={onMutationConfirm}
        onClose={() => {
          if (!mutationBusy) setMutationOpen(false);
        }}
      />
    </>
  );
}
